import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PipelineVisualization = ({ leads, onStageChange }) => {
  const [draggedLead, setDraggedLead] = useState(null);

  const stages = [
    { id: 'new', name: 'New', color: 'bg-blue-500', count: 0 },
    { id: 'contacted', name: 'Contacted', color: 'bg-yellow-500', count: 0 },
    { id: 'qualified', name: 'Qualified', color: 'bg-green-500', count: 0 },
    { id: 'proposal', name: 'Proposal', color: 'bg-purple-500', count: 0 },
    { id: 'negotiation', name: 'Negotiation', color: 'bg-orange-500', count: 0 },
    { id: 'closed-won', name: 'Closed Won', color: 'bg-emerald-500', count: 0 },
    { id: 'closed-lost', name: 'Closed Lost', color: 'bg-red-500', count: 0 }
  ];

  // Calculate lead counts per stage
  const stagesWithCounts = stages?.map(stage => ({
    ...stage,
    count: leads?.filter(lead => lead?.status === stage?.id)?.length,
    leads: leads?.filter(lead => lead?.status === stage?.id)
  }));

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e?.preventDefault();
    if (draggedLead && draggedLead?.status !== stageId) {
      onStageChange(draggedLead?.id, stageId);
    }
    setDraggedLead(null);
  };

  const calculateValue = (stage) => {
    return stage?.leads?.reduce((total, lead) => total + (lead?.estimatedValue || 0), 0);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Pipeline Overview</h2>
          <p className="text-sm text-muted-foreground">
            Drag and drop leads between stages to update their status
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="BarChart3">
          View Analytics
        </Button>
      </div>
      {/* Desktop Pipeline */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-7 gap-4">
          {stagesWithCounts?.map((stage) => (
            <div
              key={stage?.id}
              className="bg-muted rounded-lg p-4 min-h-[400px] transition-colors duration-200 hover:bg-muted/80"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage?.id)}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${stage?.color}`} />
                  <h3 className="font-medium text-foreground text-sm">{stage?.name}</h3>
                </div>
                <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded-full">
                  {stage?.count}
                </span>
              </div>

              {/* Stage Value */}
              <div className="mb-4 p-2 bg-background rounded text-center">
                <p className="text-xs text-muted-foreground">Total Value</p>
                <p className="font-semibold text-foreground">
                  ${calculateValue(stage)?.toLocaleString()}
                </p>
              </div>

              {/* Lead Cards */}
              <div className="space-y-2">
                {stage?.leads?.slice(0, 5)?.map((lead) => (
                  <div
                    key={lead?.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead)}
                    className="bg-background border border-border rounded p-3 cursor-move hover:shadow-elevation-1 transition-all duration-200"
                  >
                    <p className="font-medium text-foreground text-sm truncate">
                      {lead?.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {lead?.company}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-medium text-primary">
                        Score: {lead?.score}
                      </span>
                      {lead?.estimatedValue && (
                        <span className="text-xs text-muted-foreground">
                          ${lead?.estimatedValue?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                
                {stage?.leads?.length > 5 && (
                  <div className="text-center py-2">
                    <span className="text-xs text-muted-foreground">
                      +{stage?.leads?.length - 5} more
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Mobile Pipeline */}
      <div className="lg:hidden space-y-4">
        {stagesWithCounts?.map((stage) => (
          <div key={stage?.id} className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${stage?.color}`} />
                <h3 className="font-medium text-foreground">{stage?.name}</h3>
                <span className="text-sm text-muted-foreground bg-background px-2 py-1 rounded-full">
                  {stage?.count}
                </span>
              </div>
              <span className="text-sm font-semibold text-foreground">
                ${calculateValue(stage)?.toLocaleString()}
              </span>
            </div>
            
            {stage?.leads?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {stage?.leads?.slice(0, 4)?.map((lead) => (
                  <div
                    key={lead?.id}
                    className="bg-background border border-border rounded p-2"
                  >
                    <p className="font-medium text-foreground text-sm truncate">
                      {lead?.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {lead?.company}
                    </p>
                  </div>
                ))}
                
                {stage?.leads?.length > 4 && (
                  <div className="bg-background border border-border rounded p-2 flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">
                      +{stage?.leads?.length - 4} more
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Pipeline Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-muted rounded-lg p-4 text-center">
          <Icon name="Users" size={20} className="text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{leads?.length}</p>
          <p className="text-xs text-muted-foreground">Total Leads</p>
        </div>
        
        <div className="bg-muted rounded-lg p-4 text-center">
          <Icon name="TrendingUp" size={20} className="text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">
            {stagesWithCounts?.find(s => s?.id === 'qualified')?.count || 0}
          </p>
          <p className="text-xs text-muted-foreground">Qualified</p>
        </div>
        
        <div className="bg-muted rounded-lg p-4 text-center">
          <Icon name="DollarSign" size={20} className="text-yellow-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">
            ${stagesWithCounts?.reduce((total, stage) => total + calculateValue(stage), 0)?.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Pipeline Value</p>
        </div>
        
        <div className="bg-muted rounded-lg p-4 text-center">
          <Icon name="Target" size={20} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">
            {Math.round((stagesWithCounts?.find(s => s?.id === 'closed-won')?.count || 0) / leads?.length * 100) || 0}%
          </p>
          <p className="text-xs text-muted-foreground">Win Rate</p>
        </div>
      </div>
    </div>
  );
};

export default PipelineVisualization;